@use 'sass:map';

@use '../../../scss/tokens/line-heights';
@use '../../../scss/global/spacing';
@use '../../../scss/global/border';
@use '../../../scss/global/color';

@use './checkbox.mixins' as mixins;

:host {
  /**
  * @prop --text-color: The text color of the checkbox
  */
  --text-color: var(--fds-color-common-black30, #{color.$default-black-30});

  /**
  * @prop --text-color-disabled: The text color of the checkbox when disabled
  */
  --text-color-disabled: var(--fds-color-common-black100, #{color.$default-black-100});

  /**
  * @prop --background-color-hover: The background color of the checkbox when hovered
  */
  --background-color-hover: var(--fds-color-common-black180, #{color.$default-black-180});

  /**
  * @prop --background-color-focus: The background color of the checkbox when focused
  */
  --background-color-focus: var(--fds-color-common-black190, #{color.$default-black-190});

  /**
  * @prop --border-color-focus: The border color of the checkbox when focused
  */
  --border-color-focus: var(--fds-color-common-white100, #{color.$default-white-100});

  /**
  * @prop --checkbox-color: The color of the checkbox icon
  */
  --checkbox-color: var(--fds-color-common-secondary100, #{color.$default-secondary-100});

  /**
  * @prop --checkbox-color-disabled: The color of the checkbox icon when disabled
  */
  --checkbox-color-disabled: var(--fds-color-common-black150, #{color.$default-black-150});

  /**
  * @prop --frame-color: The color of the surrounding frame/border if set using the frame property
  */
  --frame-color: var(--fds-color-common-black180, #{color.$default-black-180});

  /**
  * @prop --required-asterisk-color: The required asterisk color of the input.
  */
  --required-asterisk-color: var(--fds-color-common-red100, #{color.$default-red-100});

  .icon {
    display: flex;
    padding: spacing.$spacer-1 0;
    gap: 8px;
    align-items: center;
    flex-direction: row;
    line-height: map.get(line-heights.$default, size-text-common-line-height-1);
  }

  .icon--reversed {
    flex-direction: row-reverse;
  }

  .fds-checkbox__required {
    color: var(--required-asterisk-color);
  }
}

:host(.dark-mode) {
  --text-color: var(--fds-color-common-white100, #{color.$default-white-100});
  --text-color-disabled: var(--fds-color-common-black110, #{color.$default-black-110});
  --background-color-hover: var(--fds-color-common-black50, #{color.$default-black-50});
  --background-color-focus: var(--fds-color-common-black60, #{color.$default-black-60});
  --border-color-focus: var(--fds-color-common-black20, #{color.$default-black-20});
  --checkbox-color: var(--fds-color-common-secondary160, #{color.$default-secondary-160});
  --checkbox-color-disabled: var(--fds-color-common-black100, #{color.$default-black-100});
  --required-asterisk-color: var(--fds-color-common-red100, #{color.$default-red-100});
}

.checkbox-container {
  display: flex;
  align-items: center;
  position: relative;
  user-select: none;
  border: 2px solid var(--fds-color-common-transparent100, color.$default-transparent);
  border-radius: border.$radius-1;
  box-sizing: border-box;
  width: fit-content;

  @include mixins.checkbox-text;

  &.disabled {
    color: var(--text-color-disabled);
    cursor: default;

    .icon {
      fds-icon {
        color: var(--checkbox-color-disabled);
      }
    }
  }

  @include mixins.checkbox-icon;
  @include mixins.checkbox-input;
}

:host .checkbox-container:not(.disabled) .icon:hover {
  fds-icon {
    background: var(--fds-color-common-secondary180, #{color.$default-secondary-180});
    border-radius: border.$radius-1;
  }

  span {
    font-weight: bold;
  }
}

:host(.dark-mode) .checkbox-container:not(.disabled) .icon:hover {
  fds-icon {
    background: var(--fds-color-common-secondary80, #{color.$default-secondary-80});
  }
}

:host .checkbox-container:not(.disabled) .icon:focus-within fds-icon:focus {
  outline: 2px solid var(--fds-color-common-blue100, color.$default-blue-100);
  box-shadow: 0 0 0 2px var(--border-color-focus) inset;
  border-radius: 6px;
}
